import { AbstractControl, ValidationErrors } from '@angular/forms';
import { AsyncValidatorFn, ValidatorFn } from '@angular/forms/forms';
import { addMinutes } from 'date-fns';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { DutyLogHttpClient } from 'src/app/shared/flight-log-api/duty-logs/duty-log-client.service';
import { dutyLogIdoriginatesFromGoldenHour } from 'src/store/duty-log/duty-log.model';
import { toggleErrors } from 'src/util/angular/ng-forms-validation';
import {
  formatMomentDate,
  getDurationInMsOrDefault,
  millisecondsInHour,
  parseDateOrDefault,
  toDate,
} from 'src/util/dateLib';
import { isNullOrWhitespace } from 'src/util/string';

export function getDutyTimeDuration({
  dutyPeriodStartTime,
  dutyPeriodEndTime,
}: {
  dutyPeriodStartTime: any;
  dutyPeriodEndTime: any;
}): number | undefined {
  return getDurationInMsOrDefault(
    parseDateOrDefault(dutyPeriodStartTime),
    parseDateOrDefault(dutyPeriodEndTime),
  );
}

export function getTimeDurationBetweenShift({
  lastPeriodEndTime,
  periodStartTime,
}: {
  lastPeriodEndTime: any;
  periodStartTime: any;
}): number | undefined {
  return getDurationInMsOrDefault(
    parseDateOrDefault(lastPeriodEndTime),
    parseDateOrDefault(periodStartTime),
  );
}

export const startTimeMustNotExeed30MinutesFromNow: ValidatorFn = (
  control: AbstractControl,
): ValidationErrors | null => {
  if (control != null && control.value != null && exceeds30MinutesFromNow(control.value)) {
    const error = {
      startTime: {
        message: `Duty on time cannot be greater than 30 minutes from now.`,
      },
    };
    return error;
  }
  return null;
};

/******************************
 * StartTime Control Validators
 ******************************/
export function startTimeMustBeGreaterThanPreviousEndTime(
  prevDutyPeriodEndTime: Date | null | undefined,
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control != null && control.value != null) {
      const startTime = toDate(control.value);
      // tslint:disable-next-line: no-non-null-assertion
      const prevEndTime = toDate(prevDutyPeriodEndTime!);
      if (startTime <= (prevEndTime != null ? prevEndTime : 0)) {
        const error = {
          startTime: {
            message: `Duty on time must be greater than previous duty off time [${formatDate(
              prevEndTime,
            )}].`,
          },
        };
        return error;
      }
    }
    return null;
  };
}

export function dutyTimeExceeds14Hours(formValue: {
  dutyPeriodStartTime: any;
  dutyPeriodEndTime: any;
}): boolean {
  const duration = getDutyTimeDuration(formValue);
  const exceeds14Hours = duration != null ? duration > 14 * millisecondsInHour : false;
  return exceeds14Hours;
}

export function restTimeLessThan10Hours(
  formValue: {
    employeeId: any;
    dutyPeriodStartTime: any;
  },
  dutyPeriodEndTime: Date | null,
): boolean {
  if (
    formValue.employeeId != null &&
    formValue.dutyPeriodStartTime != null &&
    dutyPeriodEndTime != null
  ) {
    const duration = getTimeDurationBetweenShift({
      lastPeriodEndTime: dutyPeriodEndTime,
      periodStartTime: formValue.dutyPeriodStartTime,
    });
    const exceeds10Hours = duration != null ? duration < 10 * millisecondsInHour : false;

    return exceeds10Hours;
  }

  return false;
}

export function justificationRequiredForDutyTimesExceeding14Hours(
  form: AbstractControl,
): ValidationErrors | null {
  // tslint:disable-next-line: no-non-null-assertion
  const justification = form.get('justification')!;
  const isValid = dutyTimeExceeds14Hours(form.value)
    ? !isNullOrWhitespace(justification.value)
    : true;
  toggleErrors(!isValid, justification, {
    justificationRequiredForDutyTimesExceeding14Hours: {
      message: `Please enter a justification for duty logs exceeding 14 hours.`,
    },
  });
  return null;
}
export function justificationRequiredForRestTimesLessThan10Hours(dutyPeriodEndTime: Date | null) {
  return (form: AbstractControl): ValidationErrors | null => {
    // tslint:disable-next-line: no-non-null-assertion
    const restJustification = form.get('restJustification')!;
    const isValid = restTimeLessThan10Hours(form.value, dutyPeriodEndTime)
      ? !isNullOrWhitespace(restJustification.value)
      : true;
    toggleErrors(!isValid, restJustification, {
      justificationRequiredForRestTimeLessThan10Hours: {
        message: `Please enter a justification for rest time being less than 10 hours.`,
      },
    });
    return null;
  };
}

export function cannotCreateMultipleOpenShifts(
  dutyLogHttpClient: DutyLogHttpClient,
): AsyncValidatorFn {
  return (form: AbstractControl): Observable<ValidationErrors | null> => {
    // tslint:disable-next-line: no-non-null-assertion
    const employeeIdCtrl = form.get('employeeId')!;
    // tslint:disable-next-line: no-non-null-assertion
    const endTimeCtrl = form.get('dutyPeriodEndTime')!;
    return employeeIdCtrl.value == null || endTimeCtrl.value != null
      ? of(null)
      : dutyLogHttpClient.getMany({ pilotEmployeeId: employeeIdCtrl.value }).pipe(
          map((entries) => entries.isOnDuty),
          map((isOnDuty) => {
            if (isOnDuty) {
              const errors = {
                cannotCreateMultipleOpenShifts: {
                  message: `Cannot create multiple open shifts.`,
                },
              };
              return errors;
            }
            return null;
          }),
        );
  };
}

/****************************
 * EndTime Form Validators
 ****************************/
export function endTimeMustBeGreaterThanStartTime(form: AbstractControl): ValidationErrors | null {
  // tslint:disable-next-line: no-non-null-assertion
  const startTimeCtrl = form.get('dutyPeriodStartTime')!;
  // tslint:disable-next-line: no-non-null-assertion
  const endTimeCtrl = form.get('dutyPeriodEndTime')!;
  const endTime = toDate(endTimeCtrl.value);
  const startTime = toDate(startTimeCtrl.value);
  if (endTime == null || startTime == null) {
    return null;
  }
  const isValid = endTime > startTime;
  if (!isValid) {
    const errors = {
      endTime: {
        message: `Duty off time must be greater than duty on time.`,
      },
    };
    toggleErrors(true, endTimeCtrl, errors);
  }
  return null;
}

export function endTimeMustNotExeed30MinutesFromNow(
  form: AbstractControl,
): ValidationErrors | null {
  // tslint:disable-next-line: no-non-null-assertion
  const endTimeCtrl = form.get('dutyPeriodEndTime')!;
  const endTime = toDate(endTimeCtrl.value);
  if (endTime == null) {
    return null;
  }
  const isValid = endTime != null && !exceeds30MinutesFromNow(endTime);
  if (!isValid) {
    const errors = {
      endTime: {
        message: `Duty off time cannot be greater than 30 minutes from now.`,
      },
    };
    toggleErrors(true, endTimeCtrl, errors);
  }
  return null;
}

/************************************
 * EndTime (DutyEdit) Form Validators
 ************************************/
export function endTimeMustBeGreaterThanNextStartTime(nextStartTime: Date | null) {
  return (form: AbstractControl): ValidationErrors | null => {
    // tslint:disable-next-line: no-non-null-assertion
    const endTimeCtrl = form.get('dutyPeriodEndTime')!;
    const endTime = toDate(endTimeCtrl.value);
    // tslint:disable-next-line: no-non-null-assertion
    nextStartTime = toDate(nextStartTime!);
    const isValid = nextStartTime == null || endTime < nextStartTime;
    if (!isValid) {
      const errors = {
        endTime: {
          message: `Duty off time must be less than next duty on time [${formatDate(
            nextStartTime,
          )}]`,
        },
      };
      toggleErrors(true, endTimeCtrl, errors);
    }
    return null;
  };
}

function formatDate(date: Date): string {
  return date != null ? (formatMomentDate(date, 'MM/DD/YYYY HH:mm') as string) : '';
}

function exceeds30MinutesFromNow(date: Date): boolean {
  return date.getTime() > addMinutes(date, 30).getTime();
}

export function flightLogManaged(control: AbstractControl) {
  const idValue = control.value;
  if (dutyLogIdoriginatesFromGoldenHour(idValue)) {
    return {
      goldenHourId: {
        message: 'Duty logs originating from GoldenHour cannot be modified.',
      },
    };
  }
  return null;
}
